/**
 * @file accountDeletion.ts
 * @brief Company accounts and deletion (Apple 5.1.1(v), Play; BACKEND B7.2):
 *        who sees which action, the company-name check, parsing.
 *
 * @details
 * - Personal account: Delete account (unchanged, ADR-046).
 * - Company member: "Request account deletion" only; they can't delete
 *   anything themselves (data ownership: the company's).
 * - Company admin: "Request deletion of my account" and "Delete company".
 * - Super admin: none.
 * The typed name matches the server's rule: any case, outer spaces ignored.
 * Pure (no imports) so node:test can load it.
 *
 * @references
 * - BACKEND/src/features/company/companySelfDelete.mjs, auth/deletionRequest.mjs
 * - scratch/claude-code-report.md "## B7" §2
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

export type DeletionActions = {
  deleteOwnAccount: boolean;
  requestDeletion: boolean;
  deleteCompany: boolean;
};

export function deletionActionsFor(role: string | null | undefined, managedByCompany: boolean): DeletionActions {
  if (role === "superadmin") return { deleteOwnAccount: false, requestDeletion: false, deleteCompany: false };
  if (!managedByCompany) return { deleteOwnAccount: true, requestDeletion: false, deleteCompany: false };
  const admin = role === "company_admin" || role === "admin";
  return { deleteOwnAccount: false, requestDeletion: true, deleteCompany: admin };
}

export function companyNameMatches(typed: string, companyName: string | null | undefined): boolean {
  if (!companyName) return false;
  return typed.trim().toLowerCase() === companyName.trim().toLowerCase() && typed.trim() !== "";
}

export type DeletionRequest = {
  id: string;
  status: string;
  source: string | null;
  requestedAt: string | null;
  dueAt: string | null;
};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

export function parseDeletionRequest(raw: unknown): DeletionRequest | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id) ?? (typeof r.id === "number" ? String(r.id) : null);
  if (!id) return null;
  return {
    id,
    status: str(r.status) ?? "pending",
    source: str(r.source),
    requestedAt: str(r.requestedAt),
    dueAt: str(r.dueAt),
  };
}

export type MemberDeletionRequest = {
  id: string;
  userId: string;
  name: string;
  email: string;
  reason: string | null;
  source: string | null;
  requestedAt: string | null;
  dueAt: string | null;
};

export type CompanyDeletionInfo = {
  companyName: string;
  memberCount: number;
  warning: string;
  confirmHint: string;
  memberRequests: MemberDeletionRequest[];
};

export function parseCompanyDeletionInfo(raw: unknown): CompanyDeletionInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const companyName = str(r.companyName);
  const warning = str(r.warning);
  if (!companyName || !warning) return null;
  const requests = Array.isArray(r.memberRequests) ? r.memberRequests : [];
  return {
    companyName,
    memberCount: typeof r.memberCount === "number" ? r.memberCount : 0,
    warning,
    confirmHint: str(r.confirmHint) ?? "Type the company name to confirm.",
    memberRequests: requests.flatMap((m) => {
      if (!m || typeof m !== "object") return [];
      const x = m as Record<string, unknown>;
      const id = x.id == null ? null : String(x.id);
      const userId = x.userId == null ? null : String(x.userId);
      if (!id || !userId) return [];
      return [
        {
          id,
          userId,
          name: str(x.name) ?? str(x.email) ?? "Member",
          email: str(x.email) ?? "",
          reason: str(x.reason),
          source: str(x.source),
          requestedAt: str(x.requestedAt),
          dueAt: str(x.dueAt),
        },
      ];
    }),
  };
}

/** "Requested on 22 Oct 2026 · done by 21 Nov 2026". */
export function requestStatusLine(req: DeletionRequest, formatDate: (iso: string) => string): string {
  const on = req.requestedAt ? `Requested on ${formatDate(req.requestedAt)}` : "Requested";
  return req.dueAt ? `${on} · done by ${formatDate(req.dueAt)}` : on;
}
